/**
 * A very small validator, written by hand instead of pulled in.
 *
 * The protocol is shared between a Node server and a browser bundle that has
 * to load on a phone over a slow connection, and a general purpose schema
 * library costs more in shipped bytes than every message definition in this
 * package put together. Roughly a hundred lines buys the same guarantee for
 * this one job, with error paths good enough to debug from a log line.
 *
 * Two rules shape the design.
 *
 * Unknown keys are dropped, never rejected. A wire protocol has two ends that
 * are deployed at different times, and a server that refuses a message because
 * a newer client added a field it does not care about turns every rollout into
 * an outage. What comes out of a validator is a fresh object holding only the
 * fields that were asked for, so nothing unvalidated reaches the rest of the
 * program either.
 *
 * Failure is a value, not an exception, at the boundary. Inside the validators
 * a throw is the readable way to unwind, but `parse` converts it, because
 * every caller here is handling untrusted input and has to deal with the bad
 * case anyway.
 */

export interface Ok<T> {
  ok: true
  value: T
}

export interface Fail {
  ok: false
  /** Where the failure was, as a dotted path. Empty string means the root. */
  path: string
  message: string
}

export type Parsed<T> = Ok<T> | Fail

/** A validator: takes anything, returns a narrowed value or throws internally. */
export interface Shape<T> {
  (value: unknown, path: string): T
}

class ShapeError extends Error {
  readonly path: string

  constructor(path: string, message: string) {
    super(path === '' ? message : `${path}: ${message}`)
    this.name = 'ShapeError'
    this.path = path
  }
}

function fail(path: string, message: string): never {
  throw new ShapeError(path, message)
}

/** Runs a validator over untrusted input. The only entry point callers need. */
export function parse<T>(shape: Shape<T>, value: unknown): Parsed<T> {
  try {
    return { ok: true, value: shape(value, '') }
  } catch (error) {
    if (error instanceof ShapeError) {
      return { ok: false, path: error.path, message: error.message }
    }
    throw error
  }
}

/** The type a validator produces. Saves restating every message shape twice. */
export type Infer<S> = S extends Shape<infer T> ? T : never

function join(path: string, key: string): string {
  return path === '' ? key : `${path}.${key}`
}

export interface StringOptions {
  min?: number
  max?: number
  pattern?: RegExp
}

/**
 * Every string on this wire is length-capped. A field with no maximum is an
 * invitation to send a hundred megabytes of it, and the cap has to live at the
 * edge rather than in a database column constraint that runs much later.
 */
export function string(options: StringOptions = {}): Shape<string> {
  const { min = 0, max = 4096, pattern } = options
  return (value, path) => {
    if (typeof value !== 'string') fail(path, `expected a string, got ${describe(value)}`)
    if (value.length < min) fail(path, `expected at least ${min} characters`)
    if (value.length > max) fail(path, `expected at most ${max} characters`)
    if (pattern && !pattern.test(value)) fail(path, 'does not match the expected format')
    return value
  }
}

export interface NumberOptions {
  min?: number
  max?: number
}

export function integer(options: NumberOptions = {}): Shape<number> {
  const { min = Number.MIN_SAFE_INTEGER, max = Number.MAX_SAFE_INTEGER } = options
  return (value, path) => {
    if (typeof value !== 'number' || !Number.isInteger(value)) {
      fail(path, `expected a whole number, got ${describe(value)}`)
    }
    if (value < min) fail(path, `expected at least ${min}`)
    if (value > max) fail(path, `expected at most ${max}`)
    return value
  }
}

/**
 * A number that is allowed a fractional part.
 *
 * Separate from `integer` rather than an option on it, because the two guard
 * different mistakes and the caller has to say which one it means. An
 * indicator value is 62.4 percent, or 1.8 children per household, and rounding
 * one of those to satisfy a validator quietly changes what a programme
 * reported.
 *
 * NaN and the infinities are refused by name rather than left to the bounds
 * check. All three are numbers as far as `typeof` is concerned, and every
 * comparison against NaN is false, so `value < min` waves it straight through.
 * What it reaches is a numeric database column, where NaN sorts outside every
 * range and matches no equality test, and the row holding it becomes invisible
 * to the report it was filed for.
 */
export function number(options: NumberOptions = {}): Shape<number> {
  /*
   * Unbounded by default, unlike `integer`, which can lean on the safe integer
   * range. There is no span that is right for every measure a programme might
   * track — a count of households and a rainfall figure in millimetres share
   * nothing — and a default cap invented here would reject real data with an
   * error nobody could act on. Callers that know their range pass it.
   */
  const { min = Number.NEGATIVE_INFINITY, max = Number.POSITIVE_INFINITY } = options
  return (value, path) => {
    if (typeof value !== 'number') fail(path, `expected a number, got ${describe(value)}`)
    if (Number.isNaN(value)) fail(path, 'expected a number, got NaN')
    if (!Number.isFinite(value)) fail(path, 'expected a finite number, got an infinity')
    if (value < min) fail(path, `expected at least ${min}`)
    if (value > max) fail(path, `expected at most ${max}`)
    return value
  }
}

/**
 * The longest address this accepts.
 *
 * RFC 5321 caps a reverse-path at 256 octets including the angle brackets,
 * which leaves 254 for the address. Nothing legitimate is anywhere near it,
 * and the cap is here so that the pattern below is only ever run against a
 * bounded string.
 */
export const MAX_EMAIL_LENGTH = 254

/**
 * Practical, and deliberately not RFC 5322.
 *
 * The grammar in the RFC admits comments, quoted local parts, and folding
 * whitespace, and the regular expressions that implement it faithfully are
 * thousands of characters long and are a reliable source of catastrophic
 * backtracking. What this asks is: something, then one @, then a domain of at
 * least two dot-separated labels, with no whitespace and no second @ anywhere.
 * That rejects every address a person actually mistypes.
 *
 * Each repeated group has to consume a literal dot before it can run again, so
 * there is no ambiguity between iterations and no backtracking to exploit.
 *
 * The known gap is that it accepts a few local parts a strict reader would
 * refuse, such as one with a leading dot. Being slightly generous costs a
 * bounced invitation; being strict costs a real person their account, and the
 * mail server is the only thing that can settle the question anyway.
 */
const EMAIL = /^[^\s@]{1,64}@[^\s@.]+(?:\.[^\s@.]+)+$/

/**
 * An email address, normalised to something that can be a unique key.
 *
 * The normalisation is the point. An address is the identity of a staff user
 * here — it is what an invitation is sent to and what a login is looked up by
 * — so two spellings of one mailbox mean two accounts, and the person who
 * signed up as `Amina@example.org` cannot accept the invitation sent to
 * `amina@example.org`, cannot reset a password, and shows up twice in the
 * staff list with one of the two able to do nothing at all.
 */
export function email(options: { max?: number } = {}): Shape<string> {
  const { max = MAX_EMAIL_LENGTH } = options
  return (value, path) => {
    if (typeof value !== 'string') fail(path, `expected an email address, got ${describe(value)}`)
    /*
     * Trimmed before anything else. A phone keyboard's autocomplete and a
     * paste out of a spreadsheet both bring a trailing space, and refusing it
     * produces "this is not a valid email address" pointing at something that
     * looks exactly right on screen.
     */
    const trimmed = value.trim()
    if (trimmed.length > max) fail(path, `expected at most ${max} characters`)
    if (!EMAIL.test(trimmed)) fail(path, 'is not a valid email address')
    /*
     * Lowercased with `toLowerCase` and not `toLocaleLowerCase`: the
     * locale-aware one maps a Turkish dotted capital I to a character that is
     * not ASCII i, so the same address would normalise two different ways
     * depending on the locale the process happened to start in.
     *
     * The cost, stated plainly: RFC 5321 does permit a case-sensitive local
     * part, so an address that genuinely distinguishes them would be folded
     * here. No mail provider anybody uses implements that, and the duplicate
     * accounts this prevents are a certainty where the address it would break
     * is a rumour.
     */
    return trimmed.toLowerCase()
  }
}

/**
 * An ISO-8601 date, or a date and time with an offset.
 *
 * `2026-03-14` or `2026-03-14T09:30:00Z`, and nothing else.
 *
 * An offset is required whenever a time is present, and that is the rule worth
 * arguing for. `2026-03-14T09:30:00` names a different instant in Kano than it
 * does in London, and there is no correct way for a server to guess which one
 * a client meant: assume UTC and a case event filed at half past nine in the
 * morning is stamped half past nine somewhere else, which is wrong on the
 * timeline that is supposed to be the record. Refusing it makes the client
 * state what it knows, and `Date.prototype.toISOString` already does.
 *
 * A space in place of the `T` is also refused, though RFC 3339 allows it by
 * agreement. It is what Postgres prints, so a value in that form is a sign
 * that somebody serialised a row straight onto the wire rather than converting
 * it, and that is worth catching rather than accommodating.
 */
const ISO_DATE =
  /^(\d{4})-(\d{2})-(\d{2})(?:T(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d{1,9})?)?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d))?$/

export function isoDate(): Shape<string> {
  return (value, path) => {
    if (typeof value !== 'string') fail(path, `expected a date string, got ${describe(value)}`)
    const match = ISO_DATE.exec(value)
    if (!match) fail(path, 'is not an ISO-8601 date or date-time with an offset')

    const year = Number(match[1])
    const month = Number(match[2])
    const day = Number(match[3])
    /*
     * The calendar is checked by hand, because `Date` will not do it. Asked
     * for 2026-02-30 it answers the first of March rather than refusing, so a
     * mistyped day in a case record would be stored, silently, as a different
     * day than the one that was typed, and nothing downstream could ever tell.
     *
     * Building the date and comparing the parts back is the whole check: any
     * component that had to roll over comes back different.
     */
    const asUtc = new Date(Date.UTC(year, month - 1, day))
    const isRealDate =
      asUtc.getUTCFullYear() === year &&
      asUtc.getUTCMonth() === month - 1 &&
      asUtc.getUTCDate() === day
    if (!isRealDate) fail(path, 'is not a real date')

    /*
     * Returned as the string that arrived, not as a `Date`. The wire format is
     * a string, and a validator that hands back a `Date` has quietly decided
     * which instant an offset-carrying value refers to and thrown away the
     * offset the client sent. Parsing belongs to whoever knows what the value
     * is for; this only promises the text is a date.
     */
    return value
  }
}

export function boolean(): Shape<boolean> {
  return (value, path) => {
    if (typeof value !== 'boolean') fail(path, `expected true or false, got ${describe(value)}`)
    return value
  }
}

export function literal<const T extends string>(expected: T): Shape<T> {
  return (value, path) => {
    if (value !== expected) fail(path, `expected "${expected}", got ${describe(value)}`)
    return expected
  }
}

/** One of a fixed set of strings. The values double as the runtime whitelist. */
export function oneOf<const T extends readonly string[]>(allowed: T): Shape<T[number]> {
  return (value, path) => {
    if (typeof value !== 'string' || !allowed.includes(value)) {
      fail(path, `expected one of ${allowed.join(', ')}, got ${describe(value)}`)
    }
    return value
  }
}

/** One of a fixed set of numbers. The numeric twin of `oneOf`. */
export function oneOfNumber<const T extends readonly number[]>(allowed: T): Shape<T[number]> {
  return (value, path) => {
    if (typeof value !== 'number' || !allowed.includes(value)) {
      fail(path, `expected one of ${allowed.join(', ')}, got ${describe(value)}`)
    }
    return value
  }
}

export function array<T>(item: Shape<T>, options: { max?: number } = {}): Shape<T[]> {
  const { max = 64 } = options
  return (value, path) => {
    if (!Array.isArray(value)) fail(path, `expected a list, got ${describe(value)}`)
    if (value.length > max) fail(path, `expected at most ${max} entries`)
    return value.map((entry, index) => item(entry, join(path, String(index))))
  }
}

export interface RecordOptions {
  maxKeys?: number
  maxKeyLength?: number
}

/**
 * A map whose keys are data rather than schema.
 *
 * For the disaggregation maps: `{ female: 412, male: 388 }`, or
 * `{ 'kano:18-24': 96 }`. Which dimensions a project breaks an indicator down
 * by is the project's choice, so the keys cannot be listed in advance the way
 * `object` needs them to be.
 *
 * Both the number of keys and the length of each are capped, because this is
 * the only validator here whose input size is not already bounded by its own
 * definition. `object` reads a fixed list of fields; `array` counts entries. A
 * record with no cap will build a hundred thousand element map out of one
 * request body and hand it to a JSON column, and the request that does it fits
 * in a few hundred kilobytes.
 */
export function record<T>(item: Shape<T>, options: RecordOptions = {}): Shape<Record<string, T>> {
  const { maxKeys = 64, maxKeyLength = 64 } = options
  return (value, path) => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      fail(path, `expected an object, got ${describe(value)}`)
    }
    const entries = Object.entries(value as Record<string, unknown>)
    if (entries.length > maxKeys) fail(path, `expected at most ${maxKeys} keys`)

    const result: Record<string, T> = {}
    for (const [key, raw] of entries) {
      if (key.length === 0) fail(path, 'has an empty key')
      // The key itself is not echoed back. It is attacker-chosen text and the
      // length is the whole complaint, so repeating it adds nothing but a
      // longer log line.
      if (key.length > maxKeyLength) fail(path, `has a key over ${maxKeyLength} characters`)
      /*
       * Written with `defineProperty` rather than `result[key] = ...`, because
       * here the keys come from the request. `JSON.parse` makes `__proto__` an
       * ordinary own property, so it arrives through `Object.entries` looking
       * like any other key, and a plain assignment would hand it to the setter
       * that swaps the object's prototype instead of storing anything.
       * `object` is safe from this for free, because it walks its declared
       * fields and never sees a key it was not expecting; this one has no
       * declared fields to walk.
       */
      Object.defineProperty(result, key, {
        value: item(raw, join(path, key)),
        enumerable: true,
        writable: true,
        configurable: true,
      })
    }
    return result
  }
}

/**
 * Marks a field as allowed to be absent.
 *
 * `undefined` and a missing key mean the same thing here, and `null` is
 * accepted as absent too, because JSON round trips through languages that only
 * have one of the two and it is not worth an error for a client that picked
 * the other one.
 *
 * That last leniency is exactly wrong for a field somebody wants to clear,
 * which is what `nullable` is for: wrapping the inner shape in it tells this
 * one to stop folding null into absence and pass it through instead.
 */
const OPTIONAL = Symbol('optional')

/** Marks a shape for which an explicit null is a value, not a way of saying nothing. */
const NULLABLE = Symbol('nullable')

export interface OptionalShape<T> extends Shape<T | undefined> {
  [OPTIONAL]: true
  [NULLABLE]?: true
}

export interface NullableShape<T> extends Shape<T | null> {
  [NULLABLE]: true
}

/**
 * Accepts an explicit null, and keeps it.
 *
 * Not a synonym for `optional`, and the difference is the reason this exists.
 * `optional(string())` cannot express "clear my city": a missing key and
 * `city: null` both come out as `undefined`, so the handler cannot tell "leave
 * this alone" from "empty it", and whichever of the two it assumes is wrong
 * half the time.
 *
 * The server already depends on the distinction one layer down.
 * `profileRepository.save` builds its update with `if ('city' in fields)`,
 * specifically so that an absent key leaves the column untouched while an
 * explicit null writes null, and its own comment says why: spreading the input
 * instead would turn every unmentioned field into an `undefined` that Kysely
 * writes as null and wipe the rest of somebody's profile. A validator that
 * folds null into undefined at the edge makes all of that care pointless from
 * the other direction — the person who deletes their city has it silently kept
 * instead, and the only sign is that the field never changes.
 *
 * So a patch field is written `optional(nullable(string()))`: absent means
 * leave alone, null means clear, a value means set. A field that must always
 * be sent but may be empty is `nullable(string())` on its own.
 */
export function nullable<T>(inner: Shape<T>): NullableShape<T> {
  const shape = ((value, path) =>
    value === null ? null : inner(value, path)) as NullableShape<T>
  shape[NULLABLE] = true
  return shape
}

export function optional<T>(inner: Shape<T>): OptionalShape<T> {
  // Asked once, at construction, rather than on every value: whether the inner
  // shape wants to see null cannot change afterwards.
  const keepsNull = NULLABLE in inner
  const shape = ((value, path) => {
    if (value === undefined) return undefined
    if (value === null && !keepsNull) return undefined
    return inner(value, path)
  }) as OptionalShape<T>
  shape[OPTIONAL] = true
  // Carried up the wrapper so `object` can tell the two apart from outside.
  if (keepsNull) shape[NULLABLE] = true
  return shape
}

type Fields = Record<string, Shape<unknown>>

type ObjectOf<F extends Fields> = {
  [K in keyof F as F[K] extends OptionalShape<unknown> ? never : K]: Infer<F[K]>
} & {
  [K in keyof F as F[K] extends OptionalShape<unknown> ? K : never]?: Infer<F[K]>
}

/**
 * An object with exactly the listed fields. Unknown keys are dropped, and an
 * optional field that was absent stays absent rather than becoming an explicit
 * `undefined`, which keeps the result clean under `exactOptionalPropertyTypes`.
 */
export function object<F extends Fields>(fields: F): Shape<ObjectOf<F>> {
  const entries = Object.entries(fields)
  return (value, path) => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      fail(path, `expected an object, got ${describe(value)}`)
    }
    const source = value as Record<string, unknown>
    const result: Record<string, unknown> = {}
    for (const [key, shape] of entries) {
      const raw = source[key]
      const isOptional = OPTIONAL in shape
      if (isOptional && raw === undefined) continue
      /*
       * A plain optional field treats null as absence and is skipped; one
       * wrapped in `nullable` wants the null and falls through to the
       * validator, which returns it. Without this line
       * `optional(nullable(x))` would be a long-winded spelling of
       * `optional(x)`, because the key would be dropped here before the shape
       * ever ran.
       */
      if (isOptional && raw === null && !(NULLABLE in shape)) continue
      if (!isOptional && raw === undefined) fail(join(path, key), 'is required')
      result[key] = shape(raw, join(path, key))
    }
    return result as ObjectOf<F>
  }
}

/**
 * A tagged union, keyed on a discriminant field.
 *
 * Looking the variant up by tag rather than trying each one in turn is what
 * makes the error useful: "unknown message type" points at the real problem,
 * where a list of every variant's complaint does not.
 */
export function variant<K extends string, M extends Record<string, Shape<unknown>>>(
  key: K,
  members: M,
): Shape<Infer<M[keyof M]>> {
  const names = Object.keys(members)
  return (value, path) => {
    if (typeof value !== 'object' || value === null) {
      fail(path, `expected an object, got ${describe(value)}`)
    }
    const tag = (value as Record<string, unknown>)[key]
    if (typeof tag !== 'string') fail(join(path, key), 'is required')
    const member = Object.hasOwn(members, tag) ? members[tag] : undefined
    if (!member) {
      fail(join(path, key), `unknown value "${tag}", expected one of ${names.join(', ')}`)
    }
    return member(value, path) as Infer<M[keyof M]>
  }
}

/** A short, safe rendering of a bad value for an error message. */
function describe(value: unknown): string {
  if (value === null) return 'null'
  if (Array.isArray(value)) return 'a list'
  const type = typeof value
  if (type === 'string') return 'a string'
  if (type === 'object') return 'an object'
  return type
}
